import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import type { MessageKey } from '@reos/i18n';
import { AuthApiError, changePassword, hasPasswordAccount } from '../lib/api.js';
import { V4FormRow, V4Group, V4Switch } from './ux-v4.js';

/** Musi sedet s `minPasswordLength: 12` v auth-factory.ts (identity) — stejna pravidla jako registrace/reset. */
const MIN_PASSWORD_LENGTH = 12;

/**
 * CO SE STALO, NE „nezdarilo se" (krok E2EFIX3, nalez e2e S18i).
 *
 * Zmena hesla ma tri konce a kazdy ma vlastni vetu:
 *  - `INVALID_PASSWORD` z Better Auth — soucasne heslo nesouhlasi,
 *  - zakaz REOS pred handlerem BA (dnes `demo/blocked` z DEMO11) — hlasku uz
 *    poslal server v `title` problem+json a je lokalizovana, takze se bere
 *    PRIMO; druha kopie textu v adminu by se s katalogem rozesla,
 *  - cokoli jineho — obecna veta (sitova chyba, 500, neznamy stav).
 *
 * Do E2EFIX3 spadly druhe dva pripady do jednoho: makléřka v DEMO kancelari
 * videla „Změna hesla se nezdařila. Zkuste to prosím znovu." a zkousela to
 * znovu nad zakazem, ktery se nikdy nezmeni.
 */
function chybaZmenyHesla(error: unknown, t: (key: MessageKey) => string): string {
  if (!(error instanceof AuthApiError)) return t('webAdmin.settings.passwordGenericError');
  if (error.code === 'INVALID_PASSWORD') return t('webAdmin.settings.passwordInvalidCurrent');
  if (error.code === DEMO_BLOCKED_CODE && error.message !== '') return error.message;
  return t('webAdmin.settings.passwordGenericError');
}

/** `type` problem+json zakazu v ukazce (`DEMO_BLOCKED_PROBLEM_CODE` v identity). */
const DEMO_BLOCKED_CODE = 'demo/blocked';

/**
 * Krok UXP8 — Nastaveni -> Ucet -> karta „Heslo". Pouziva existujici Better
 * Auth endpoint `change-password` (zadny vlastni endpoint, zadny primy
 * zapis do DB). Uzivatel bez hesla (jen passkey/magic link) misto formulare
 * vidi jen vetu, ze heslo nema nastavene (`hasPasswordAccount`).
 */
export function PasswordSettingsCard(): React.JSX.Element {
  const t = useT();
  const accounts = useQuery({ queryKey: ['nastaveni', 'has-password-account'], queryFn: hasPasswordAccount });
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [revokeOtherSessions, setRevokeOtherSessions] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (currentPassword === '') {
      setError(t('webAdmin.settings.passwordCurrentRequired'));
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(t('webAdmin.settings.passwordTooShort', { min: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('webAdmin.settings.passwordMismatch'));
      return;
    }
    setBusy(true);
    try {
      await changePassword({ currentPassword, newPassword, revokeOtherSessions });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess(true);
    } catch (e) {
      setError(chybaZmenyHesla(e, t));
    } finally {
      setBusy(false);
    }
  }

  if (accounts.data === false) {
    return (
      <div className="rh-card" style={{ maxWidth: 640, padding: 16, marginBottom: 16 }} data-testid="nastaveni-password-card">
        <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.passwordCardTitle')}</h2>
        <p className="text-sm text-gray-600" data-testid="password-no-account">
          {t('webAdmin.settings.passwordNoPasswordSet')}
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, marginBottom: 16 }} data-testid="nastaveni-password-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.passwordCardTitle')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.settings.passwordCardPerex')}</p>
      <form onSubmit={(e) => void onSubmit(e)} data-testid="password-form">
        <V4Group testId="password-form-group">
          <V4FormRow stack label={t('webAdmin.settings.passwordCurrentLabel')}>
            <input
              className="rh-input"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="password-current"
            />
          </V4FormRow>
          <V4FormRow stack label={t('webAdmin.settings.passwordNewLabel')}>
            <input
              className="rh-input"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="password-new"
            />
          </V4FormRow>
          <V4FormRow stack label={t('webAdmin.settings.passwordConfirmLabel')}>
            <input
              className="rh-input"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="password-confirm"
            />
          </V4FormRow>
          <V4FormRow label={t('webAdmin.settings.passwordRevokeOtherSessionsLabel')}>
            <V4Switch
              checked={revokeOtherSessions}
              onChange={setRevokeOtherSessions}
              label={t('webAdmin.settings.passwordRevokeOtherSessionsLabel')}
              testId="password-revoke-other-sessions"
            />
          </V4FormRow>
        </V4Group>
        {error !== null ? (
          <p role="alert" className="mt-2 text-sm text-red-600" data-testid="password-error">
            {error}
          </p>
        ) : null}
        {success ? (
          <p role="status" className="mt-2 text-sm text-green-700" data-testid="password-success">
            {t('webAdmin.settings.passwordSuccess')}
          </p>
        ) : null}
        <button type="submit" className="rh-v4-btn rh-v4-btn--filled mt-3" disabled={busy} data-testid="password-submit">
          {t('webAdmin.settings.passwordSubmitButton')}
        </button>
      </form>
    </div>
  );
}
